import React, { useEffect, ReactElement, useRef } from 'react';
import { toast } from 'react-toastify';
import { LiveProps } from '../../LiveContainer';
import { LiveAudiences } from '../LiveAudiences/LiveAudiences';
import { LiveHeader } from '../LiveHeader/LiveHeader';
import { LiveMediaPlayerHost } from '../LiveMediaPlayerHost/LiveMediaPlayerHost';
import { LiveRecord } from '../LiveRecord/LiveRecord';
import { LiveSettings } from '../LiveSettings/LiveSettings';
import { LiveGuideline } from '../LiveGuideline/LiveGuideline';
import { Prompt, useHistory, useParams } from 'react-router-dom';
import {
  useEndDeliveryLiveMutation,
  useGetLiveInfoMutation,
  useGetTermsAgreementDateQuery,
  useJoinLiveMutation,
} from '../../../../services/api/apiProvider';
import './Live.scss';
import 'react-toastify/dist/ReactToastify.css';
import { LiveMediaPlayerAudience } from '../LiveMediaPlayerAudience/LiveMediaPlayerAudience';
import { ResponseJoinLive } from '../../../../services/api/models/response/responseLive';
import { LiveMessages } from '../LiveMessages/LiveMessages';
import { LiveResult } from '../LiveResult/LiveResult';
import { LiveGift } from '../LiveGift/LiveGift';
import { LiveBoard } from '../LiveBoard/LiveBoard';
import {
  Button,
  Modal,
  Nav,
  TabContainer,
  TabContent,
  TabPane,
} from 'react-bootstrap';
import { LiveEvents } from '../LiveEvents/LiveEvents';
import { LiveGiftMenu } from '../LiveGiftMenu/LiveGiftMenu';
import { LiveTerm } from '../LiveTerm/LiveTerm';
import { useDispatch } from 'react-redux';
import { actions } from '../../liveSlice';
import LiveMissionTab from '../LiveMission/LiveMissionTab';
import { NotFound } from '../../../../common/components/NotFound';
import LiveHeaderMobile from '../LiveMobileSide/LiveHeaderMobile/LiveHeaderMobile';
import LiveFooterMobile from '../LiveMobileSide/LiveFooterMobile/LiveFooterMobile';
import { ErrorResponse } from '../../../../common/constants/globalConstants';
import {
  addUserLiveReturn,
  updateUserLiveReturn,
} from '../../../live-match/liveMatchSlice';
import LikeAnimations, {
  LikeAnimationRef,
} from '../LikeAnimation/LikeAnimation';
import { reproio } from '../../../../common/utils/reproio';

export const Live = (props: LiveProps): ReactElement => {
  const { liveId } = useParams<{ liveId: string | undefined }>();
  const history = useHistory();
  const [joinLive] = useJoinLiveMutation();
  const [getLiveInfo] = useGetLiveInfoMutation();
  const [stopDelivery] = useEndDeliveryLiveMutation();
  const { data: agreement, isLoading } = useGetTermsAgreementDateQuery();

  const dispatch = useDispatch();
  const handleCloseEventModal = (): void => {
    props.showEventModal(undefined);
  };
  const liveReactionsRef = useRef<LikeAnimationRef>();

  const pathNameRef = useRef<string>('');

  useEffect(() => {
    const checkPath = window.location.href.indexOf('/voice');
    if (checkPath > 0) {
      dispatch(actions.handleTypeLive(false));
      document.title = 'Web版everylive（エブリライブ） - voice';
    } else {
      dispatch(actions.handleTypeLive(true));
      document.title = 'Web版everylive（エブリライブ） - Live';
    }
  }, []);

  //useEffect for term
  useEffect(() => {
    if (liveId === undefined) {
      pathNameRef.current = window.location.pathname;
      if (isLoading) {
        return;
      }
      // check if the user has agreed to te Terms of Service
      if (agreement?.date) {
        props.toggleGuidelineModal(true);
      } else {
        props.toggleTermModal(true);
      }
    } else {
      if (isLoading) {
        return;
      }
    }
    // clean up code
    return (): void => {
      props.resetLiveData();
    };
  }, [isLoading]);

  // useEffect for live data
  useEffect(() => {
    fetchLiveData();
    // clean up code
    return (): void => {
      props.resetLiveData();
    };
  }, [liveId]);

  // UseEffect for listen beforeunload event
  useEffect(() => {
    if (props.liveState.role === 'host' && props.liveState.isInLive) {
      window.addEventListener('beforeunload', handleReloadAndCloseBrowser);
    }
    return (): void => {
      window.removeEventListener('beforeunload', handleReloadAndCloseBrowser);
    };
  }, [props.liveState.isInLive]);

  const fetchLiveData = () => {
    if (liveId) {
      joinLive(liveId)
        .unwrap()
        .then(async (joinLiveData: ResponseJoinLive) => {
          const liveInfo = await getLiveInfo(liveId).unwrap();
          if (liveInfo.event.id) {
            reproio('track', 'complete subscribe event', {
              event_name: liveInfo.event.name,
            });
          }
          if (liveInfo.user.picture.uri) {
            dispatch(addUserLiveReturn(liveInfo.user.picture.uri));
          }

          dispatch(
            props.updateEnableGift(
              liveInfo.canReceivedGift === false ? false : true,
            ),
          );

          props.joinLive({
            ...joinLiveData,
            live: {
              ...liveInfo,
              eventRankings: joinLiveData.live.eventRankings,
            },
          });
        })
        .catch((error: ErrorResponse) => {
          if (error.status === 400) {
            if (error.data.errorCode === 102) {
              toast.error('この配信は視聴できません');
            } else if (error.data.errorCode === 103) {
              toast.error('同アカウントのライブは視聴できません');
            } else if (error.data.errorCode === 115) {
              toast.error('自分が配信中に他の配信を視聴することはできません');
            } else {
              toast.error('この配信は既に終了しています');
            }
          } else {
            toast.error(error.data.message);
          }
          history.push('/');
          dispatch(updateUserLiveReturn(true));
        });
    }
  };

  const handleReloadAndCloseBrowser = (e: BeforeUnloadEvent) => {
    if (props.liveState.liveData) {
      stopDelivery(props.liveState.liveData.id).unwrap();
    }
    return false;
  };

  const createHeartAnimation = (anchor?: string | undefined) => {
    liveReactionsRef.current?.createHeart(anchor);
  };

  return (
    <>
      <div className="live-container">
        {/* List of modal here */}
        <LiveTerm {...props}></LiveTerm>
        <LiveGuideline {...props}></LiveGuideline>

        {/* No need live settings if it's viewer*/}
        {liveId === undefined ? (
          <LiveSettings {...props}></LiveSettings>
        ) : (
          <></>
        )}

        {props.liveState.isEnded ? <LiveResult {...props}></LiveResult> : <></>}

        <div className="live-layout">
          {/* Top side - control buttons */}
          <div className="live-layout__header">
            <LiveHeader {...props}></LiveHeader>
          </div>

          <div className="d-flex">
            {/* Left side - Audience */}
            <div className="live-layout__left">
              <LiveRecord {...props}></LiveRecord>
              <LiveAudiences {...props}></LiveAudiences>
            </div>

            {/* Center video player */}
            <div className="live-layout__center">
              <div className="live-media__content">
                <div className="live-media-player__content">
                  {props.liveState.isInLive &&
                    props.liveState.liveData &&
                    (props.liveState.role === 'host' ? (
                      <LiveMediaPlayerHost {...props}></LiveMediaPlayerHost>
                    ) : (
                      <LiveMediaPlayerAudience
                        {...props}
                        clickLike={createHeartAnimation}
                      ></LiveMediaPlayerAudience>
                    ))}

                  {props.liveState.isInLive && props.liveState.liveData && (
                    <>
                      <LiveGift {...props}></LiveGift>
                      <LiveBoard {...props} />
                    </>
                  )}
                  <LiveHeaderMobile {...props} />
                  <LiveFooterMobile {...props} />
                  <LikeAnimations
                    ref={liveReactionsRef}
                    liveState={props.liveState}
                  />
                </div>
              </div>
              {props.liveState.role === 'host' && props.liveState.isInLive && (
                <Prompt
                  message={(location, action) => {
                    return `配信を終了してもよろしいですか？`;
                  }}
                />
              )}
            </div>

            {/* Right side panel: Chat, Events */}
            <div className="live-layout__right">
              <div
                className="live-layout__right__top-panel"
                style={{
                  height: props.liveState.role === 'host' ? '100%' : '50%',
                }}
              >
                <TabContainer defaultActiveKey="chat">
                  {/* Tab Header*/}
                  <Nav
                    fill
                    variant="tabs"
                    className="box-shadow-2"
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '0.25rem 0.25rem 0px 0px',
                    }}
                  >
                    {/* Chat tab*/}
                    <Nav.Item>
                      <Nav.Link eventKey="chat">コメント</Nav.Link>
                    </Nav.Item>
                    {/* Event tab*/}
                    <Nav.Item>
                      <Nav.Link eventKey="event">イベント</Nav.Link>
                    </Nav.Item>
                    {/* Mission tab*/}
                    <Nav.Item>
                      <Nav.Link eventKey="mission">ミッション</Nav.Link>
                    </Nav.Item>
                  </Nav>
                  {/* Tab Content*/}
                  <TabContent>
                    {/* Chat */}
                    <TabPane eventKey="chat">
                      <LiveMessages {...props}></LiveMessages>
                    </TabPane>
                    {/* Events */}
                    <TabPane eventKey="event">
                      <LiveEvents {...props}></LiveEvents>
                    </TabPane>
                    {/* Mission */}
                    <TabPane eventKey="mission">
                      <LiveMissionTab></LiveMissionTab>
                    </TabPane>
                  </TabContent>
                </TabContainer>
              </div>

              {props.liveState.role === 'audience' &&
                props.liveState.isEnableGift && (
                  <div className="live-layout__right__bottom-panel">
                    <LiveGiftMenu {...props} />
                  </div>
                )}
            </div>
          </div>
        </div>

        {props.liveState.currentShowEvent !== undefined ? (
          <Modal
            show={props.liveState.showEventModal}
            onHide={handleCloseEventModal}
            backdrop="static"
            keyboard={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>{props.liveState.currentShowEvent.name}</Modal.Title>
            </Modal.Header>
            <iframe
              src={props.liveState.currentShowEvent.uri}
              className="event-modal__details"
            ></iframe>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseEventModal}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        ) : (
          <></>
        )}
      </div>
    </>
  );
};
